// The version's options as tabs (A, B, C …) and the shown option's own
// settings: its name, labour type, mark-ups and negotiating factor
// (hig_scopeoption columns). Blank = the pricing setting on the version's
// pricing date, shown as the placeholder.
import { useRef, useState } from 'react';
import { LABOUR_TYPES, OPTION_KIND, type OptionKind } from '@higgins/m2q-pricing';
import { CheckCircle2, Star, Trash2 } from 'lucide-react';
import { AddAction } from './add-action';
import type { Fields } from '../lib/device-db';
import { IN_FIELD_ORDER, moveToNextField } from '../lib/field-order';
import { SavedTextField } from './saved-text-field';
import { NumberField } from './number-field';
import { ConfirmRemoval } from './remove-button';
import { ErrorMessage } from './message';

/** Dataverse's limit for the option's name. */
const NAME_MAX = 200;

interface Props {
  options: { id: string; name: string; recommended: boolean; accepted: boolean }[];
  selectedId: string;
  onSelect: (id: string) => void;
  /** Add a copy of the shown option as a repaint or a maintenance contract (#515). null = options can't be added (a frozen version). */
  onAdd: ((kind: OptionKind) => void) | null;
  /** null = the shown option can't be recommended yet (it hasn't reached CRM). */
  onRecommend: (() => void) | null;
  /** Remove the shown option; resolves with why it couldn't be, or null when it was. null = not offered. */
  onRemove: (() => Promise<string | null>) | null;
  name: string;
  labourType: string;
  markupMaterials: number | null;
  markupOther: number | null;
  negotiatingFactor: number | null;
  /** The settings' mark-ups (%), for the placeholders. */
  defaults: { markupMaterials: number; markupOther: number };
  onChange: (fields: Fields) => void | Promise<void>;
  /** Accepted: the pricing choices can't be changed until it's unlocked. */
  locked: boolean;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-slate-600">
      {label}
      {children}
    </label>
  );
}

export function OptionBar({ options, selectedId, onSelect, onAdd, onRecommend, onRemove, name, labourType, markupMaterials, markupOther, negotiatingFactor, defaults, onChange, locked }: Props) {
  const [removing, setRemoving] = useState<'ask' | 'sending' | { error: string } | null>(null);
  const removeButton = useRef<HTMLButtonElement>(null);

  async function remove() {
    if (!onRemove) return;
    setRemoving('sending');
    const refused = await onRemove();
    setRemoving(refused ? { error: refused } : null);
  }

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Options">
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            role="tab"
            aria-selected={o.id === selectedId}
            onClick={() => onSelect(o.id)}
            className={`rounded-full border px-3 py-0.5 text-sm outdoor:min-h-11 ${o.id === selectedId ? 'border-primary bg-orange-50 font-medium text-slate-900' : 'border-slate-300 text-slate-600 hover:border-slate-400'}`}
          >
            {o.recommended && <Star className="mr-1 inline h-3.5 w-3.5 fill-primary text-primary" aria-label="Recommended" />}
            {o.name || 'Unnamed option'}
            {o.accepted && <CheckCircle2 className="ml-1 inline h-3.5 w-3.5 text-green-700" aria-label="Accepted" />}
          </button>
        ))}
        {onAdd && (
          <>
            <AddAction onClick={() => onAdd(OPTION_KIND.repaint)} className="px-1">
              Add option <span className="text-xs font-normal text-slate-500">(a copy of this one)</span>
            </AddAction>
            <AddAction onClick={() => onAdd(OPTION_KIND.maintenance)} className="px-1">
              Add maintenance contract <span className="text-xs font-normal text-slate-500">(a copy of this one)</span>
            </AddAction>
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <Field label="Name">
          <SavedTextField compact value={name} onSave={(text) => onChange({ hig_name: text })} label="Option name" maxLength={NAME_MAX} className="w-52" />
        </Field>
        {options.find((o) => o.id === selectedId)?.recommended ? (
          <span className="flex items-center gap-1 text-xs font-medium text-slate-700">
            <Star className="h-3.5 w-3.5 fill-primary text-primary" /> Recommended
          </span>
        ) : (
          <button
            type="button"
            onClick={onRecommend ?? undefined}
            disabled={!onRecommend}
            title={onRecommend ? 'Make this the option the quote recommends' : 'Once this option has synced to CRM'}
            className="flex items-center gap-1 rounded-md border border-slate-300 px-2 py-0.5 text-xs text-slate-600 hover:border-primary disabled:opacity-50"
          >
            <Star className="h-3.5 w-3.5" /> Recommend this option
          </button>
        )}
        {/* The pricing choices: locked while the option is accepted (option-lock-banner.tsx). */}
        <fieldset disabled={locked} className="contents">
          <Field label="Labour type">
            <select {...IN_FIELD_ORDER} onKeyDown={(e) => moveToNextField(e)} value={labourType} onChange={(e) => onChange({ hig_labourtype: e.target.value })} className="h-7 rounded-md border border-slate-300 bg-white px-1 text-sm">
              {LABOUR_TYPES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </Field>
          <Field label="Mark-up material">
            <NumberField compact value={markupMaterials} placeholder={String(defaults.markupMaterials)} onChange={(v) => onChange({ hig_markupmaterials: v })} label="Mark-up material %" className="w-14" />%
          </Field>
          <Field label="Mark-up other">
            <NumberField compact value={markupOther} placeholder={String(defaults.markupOther)} onChange={(v) => onChange({ hig_markupother: v })} label="Mark-up other %" className="w-14" />%
          </Field>
          <Field label="Negotiating factor">
            <NumberField compact allowNegative value={negotiatingFactor} placeholder="0" onChange={(v) => onChange({ hig_negotiatingfactor: v })} label="Negotiating factor %" className="w-14" />%
          </Field>
        </fieldset>
        <span className="text-xs text-slate-500">Blank = the CRM setting. Changes save on this device as you type and sync like the iPad&rsquo;s.</span>
        {onRemove && (
          <button ref={removeButton} type="button" onClick={() => setRemoving('ask')} className="ml-auto flex items-center gap-1 text-xs text-slate-500 hover:text-red-700">
            <Trash2 className="h-3.5 w-3.5" /> Remove option
          </button>
        )}
        <ConfirmRemoval
          open={removing === 'ask' || removing === 'sending'}
          onOpenChange={(open) => !open && setRemoving(null)}
          question={
            <>
              Remove <strong>{name || 'this option'}</strong> for good?
            </>
          }
          busy={removing === 'sending'}
          onConfirm={() => void remove()}
          returnFocus={removeButton}
        />
        {typeof removing === 'object' && removing !== null && (
          <ErrorMessage className="w-full">{removing.error}</ErrorMessage>
        )}
      </div>
    </div>
  );
}
